import { create } from 'zustand';

export interface DippyBubble {
  id: string;
  type: 'alert' | 'tip';
  title: string;
  message: string;
  action?: { label: string; href: string };
  autoMs?: number;
}

interface DippyState {
  isPanelOpen: boolean;
  activeTab: 'chat' | 'status';
  expression: 'idle' | 'alert' | 'thinking';
  bubbleQueue: DippyBubble[];
  currentBubble: DippyBubble | null;
  activeConversationId: string | null;
  badgeCount: number;

  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  setTab: (tab: 'chat' | 'status') => void;
  setExpression: (expr: 'idle' | 'alert' | 'thinking') => void;
  enqueueBubble: (bubble: Omit<DippyBubble, 'id'>) => void;
  dismissBubble: () => void;
  setActiveConversation: (id: string | null) => void;
  setBadgeCount: (count: number) => void;
}

const MAX_QUEUE = 5;

export const useDippyStore = create<DippyState>()((set) => ({
  isPanelOpen: false,
  activeTab: 'chat',
  expression: 'idle',
  bubbleQueue: [],
  currentBubble: null,
  activeConversationId: null,
  badgeCount: 0,

  openPanel: () => set({ isPanelOpen: true }),
  closePanel: () => set({ isPanelOpen: false }),
  togglePanel: () => set((s) => ({ isPanelOpen: !s.isPanelOpen })),

  setTab: (tab) => set({ activeTab: tab }),

  setExpression: (expression) => set({ expression }),

  enqueueBubble: (bubble) => {
    const id = Math.random().toString(36).slice(2);
    const newBubble = { ...bubble, id };

    set((s) => {
      const queue = [...s.bubbleQueue, newBubble].slice(-MAX_QUEUE);
      if (!s.currentBubble) {
        return { bubbleQueue: queue.slice(1), currentBubble: queue[0] };
      }
      return { bubbleQueue: queue };
    });
  },

  dismissBubble: () => {
    set((s) => {
      const [next, ...rest] = s.bubbleQueue;
      return {
        currentBubble: next ?? null,
        bubbleQueue: rest,
      };
    });
  },

  setActiveConversation: (id) => set({ activeConversationId: id }),

  setBadgeCount: (badgeCount) => set({ badgeCount }),
}));
